<template>
  <div id="dashboard" class="card dashboard">
    <header class="card-header">
      <div class="card-header-title has-text-dark is-size-4">METRICS</div>
    </header>
    <div class="card-content scrollable is-relative">
      <b-loading :is-full-page="false" :active="fetching"></b-loading>
      <div class="columns" v-if="canViewMetrics">
        <div class="column is-4">
          <b-message :type="metrics.kpiAppeals ? 'is-danger' : 'is-success'">
            <span class="title">{{ metrics.kpiAppeals }}</span>
            <br />
            <a
              class="subtitle"
              @click="$emit('filter', { kpiAppeal: 'pending' })"
            >
              KPI<br />APPEALS
            </a>
          </b-message>
        </div>
        <div class="column is-4">
          <b-message
            :type="metrics.dateChangeRequests ? 'is-danger' : 'is-success'"
          >
            <span class="title">{{ metrics.dateChangeRequests }}</span>
            <br />
            <a
              class="subtitle"
              @click="$emit('filter', { dateChangeRequest: true })"
            >
              DATE CHANGE<br />REQUESTS
            </a>
          </b-message>
        </div>
        <div class="column is-4">
          <b-message :type="metrics.unvettedNotes ? 'is-danger' : 'is-success'">
            <span class="title">{{ metrics.unvettedNotes }}</span>
            <br />
            <a
              class="subtitle"
              @click="$emit('filter', { unvettedNotes: true })"
            >
              UNVETTED<br />NOTES
            </a>
          </b-message>
        </div>
      </div>
      <div class="chart">
        <div id="containerhc" />
        <br /><br />
        <div class="columns">
          <div class="column"></div>
          <div class="column is-narrow no-wrap">
            <div class="field is-horizontal">
              <div class="field-label is-large">
                <label class="label">MODE</label>
              </div>
              <div class="field-body">
                <div class="field">
                  <div class="control">
                    <b-select v-model="statusMode" size="is-large">
                      <option value="group">Group</option>
                      <option value="individual">Individual</option>
                    </b-select>
                  </div>
                </div>
                
<!--                 <div class="field-label is-large">
                  <b-field size="is-large">
                    <b-checkbox
                      :value="filters.includeVoid"
                      @input="value => $emit('filter', { includeVoid: value })"
                    >
                      <span class="has-text-weight-bold">
                        Include Void
                      </span>
                    </b-checkbox>
                  </b-field>
                </div> -->

                <div class="field">
                  <p class="control">
                    <b-button @click="reset" size="is-large">RESET</b-button>
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="column"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { snakeCase } from "lodash";
import Highcharts from "highcharts";
import Heatmap from "highcharts/modules/heatmap";
import { statusGroups, display as displayStatus } from "@/helpers/statuses";
import { OrderService } from "@/services/order.service";
import { JsonDenormalizer } from "@/helpers/JsonDenormalizer";
import { DateFormat } from "@/helpers/date-format";

export default {
  orderService: OrderService,
  statusGroupColors: {
    OPEN: "#fff5f7",
    CLOSED: "#f6fef9",
    VOID: "#f5f5f5"
  },
  statusGroupBorderColors: {
    OPEN: "#ff6038",
    CLOSED: "#23d160",
    VOID: "#dbdbdb"
  },
  statusModes: {
    group: "group",
    individual: "individual"
  },
  modes: {
    day: "day",
    month: "month"
  },
  moment: {
    category: {
      day: "MMM-DD",
      month: "MMM-YY"
    },
    isSame: {
      day: "day",
      month: "month"
    },
    add: {
      day: "d",
      month: "M"
    },
    diff: {
      day: "days",
      month: "months"
    }
  },
  props: {
    filters: {
      type: Object,
      default: () => {}
    },
    fetchParams: {
      type: Object,
      default: () => {}
    }
  },
  data: () => ({
    statusMode: "",
    orders: [],
    metrics: {},
    fetching: false
  }),
  computed: {
    days() {
      return this.orders.reduce((acc, order) => {
        if (order.releaseDate) {
          acc.push(moment(DateFormat.toLocalDateFormat(order.releaseDate)));
        }
        return acc;
      }, []);
    },
    earliest() {
      if (this.filters && this.filters.releaseDateStart) {
        return moment(this.filters.releaseDateStart);
      }

      return moment.min(this.days);
    },
    latest() {
      if (this.filters && this.filters.releaseDateEnd) {
        return moment(this.filters.releaseDateEnd);
      }

      // return moment.max(this.days);

      // Set default to end of this month so chart will show 13 months
      return moment().endOf("month");
    },
    mode() {
      const diff = this.latest.diff(this.earliest, "days");

      if (diff >= 31) {
        return this.$options.modes.month;
      }

      return this.$options.modes.day;
    },
    range() {
      const range = [];
      let start = this.earliest;
      let end = this.latest;

      // Moment diff result for month (year?) is not exact
      if (this.mode != this.$options.modes.day) {
        start = start.startOf(this.mode);
        end = end.startOf(this.mode);
      }

      const limit = end.diff(start, this.$options.moment.diff[this.mode]);

      for (let x = 0; x <= limit; x++) {
        range.push(
          moment(this.earliest).add(x, this.$options.moment.add[this.mode])
        );
      }

      return range;
    },
    canViewMetrics() {
      return (
        this.$store.getters["Auth/hasPermission"]("experimental interface") &&
        this.$store.getters["Auth/hasPermission"]("orders dashboard metrics")
      );
    },
  },
  created() {
    this.statusMode = this.$options.statusModes.group;

    if (this.canViewMetrics) {
      this.metrics = {
        kpiAppeals: 0,
        dateChangeRequests: 0,
        unvettedNotes: 0
      };
    }
  },
  mounted() {
    Heatmap(Highcharts);
  },
  watch: {
    orders() {
      this.loadChart();
      this.borderColor();
    },
    statusMode() {
      this.loadChart();
      this.borderColor();
    }
  },
  methods: {
    borderColor() {
      let x = document.getElementsByClassName("highcharts-point");

      if (x.length) {
        Array.from(x).forEach(el => {
          if (el.getAttribute("width") == 12) {
            switch (el.getAttribute("fill")) {
              case "#fff5f7":
                el.setAttribute("stroke", "#ff6038");
                break;
              case "#f6fef9":
                el.setAttribute("stroke", "#23d160");
                break;
              default:
                el.setAttribute("stroke", "#dbdbdb");
            }
          }
        });
      }
    },
    async fetch() {
      this.fetching = true;

      try {
        const response = await this.$options.orderService.unpaginated(
          this.fetchParams
        );

        this.orders = JsonDenormalizer.denormalize(response);

        if (this.canViewMetrics) {
          const meta = response.meta;

          if (meta) {
            this.metrics.kpiAppeals = meta.pending_kpi_appeals_count || 0;
            this.metrics.dateChangeRequests =
              meta.pending_date_change_requests_count || 0;
            this.metrics.unvettedNotes = meta.unvetted_notes_count || 0;
          }
        }
      } catch (err) {}

      this.fetching = false;
    },
    reset() {
      this.statusMode = this.$options.statusModes.group;
      this.$emit("reset", {
        includeVoid: false,
        releaseDateStart: "",
        releaseDateEnd: ""
      });
    },
    loadChart() {
      let orders = {};

      // This will force sort the data to OPEN, CLOSED, VOID
      if (this.statusMode === this.$options.statusModes.group) {
        orders = {
          OPEN: [],
          CLOSED: []
        };

        if (this.filters.includeVoid) {
          orders.VOID = [];
        }
      }

      // Group orders by status (individually or grouped)
      orders = this.orders.reduce((acc, order) => {
        let status = order.status;

        if (this.statusMode === this.$options.statusModes.group) {
          const groups = Object.keys(statusGroups);

          for (let x = 0; x < groups.length; x++) {
            if (statusGroups[groups[x]].includes(status)) {
              status = groups[x];
              break;
            }
          }
        }

        if (!acc[status]) {
          acc[status] = [];
        }

        acc[status].push(order);

        return acc;
      }, orders);

      const categories = this.range.map(date =>
        date.format(this.$options.moment.category[this.mode])
      );

      // Filter grouped orders by date
      const series = Object.keys(orders).reduce((acc, status) => {
        const name = displayStatus(status);

        const data = this.range.map(date => {
          // Defaults to day mode
          let from = date.format("YYYY-MM-DD"); // user option
          let to = date.format("YYYY-MM-DD"); // user option

          if (this.mode === this.$options.modes.month) {
            from = date.startOf("month").format("YYYY-MM-DD");
            to = date.endOf("month").format("YYYY-MM-DD");
          }

          return {
            y: orders[status].filter(order =>
              date.isSame(
                moment(DateFormat.toLocalDateFormat(order.releaseDate)),
                this.$options.moment.isSame[this.mode]
              )
            ).length,
            from,
            to,
            status // user option
          };
        });

        let color = undefined;
        let borderColor = undefined;

        if (this.statusMode === this.$options.statusModes.group) {
          color = this.$options.statusGroupColors[status] || undefined;
          borderColor =
            this.$options.statusGroupBorderColors[status] || undefined;
        }

        acc.push({
          name,
          data,
          color,
          borderColor
        });

        return acc;
      }, []);

      Highcharts.chart("containerhc", {
        chart: {
          type: "column",
          events: {
            load: e => {
              const series = e.target.series[0];

              if (series) {
                const data = series.data;
                const ticks = e.target.xAxis[0].ticks;

                Object.keys(ticks).forEach((key, index) => {
                  const label = ticks[key].label;

                  if (label) {
                    const element = label.element;
                    element.onclick = () => {
                      const point = data[index];
                      const from = point.from;
                      const to = point.to;

                      this.$emit("filter", {
                        releaseDateStart: from,
                        releaseDateEnd: to
                      });
                    };
                  }
                });
              }
            }
          }
        },
        title: {
          text: "Orders by Status"
        },
        xAxis: {
          categories
        },
        yAxis: {
          title: {
            enabled: false
          }
        },
        plotOptions: {
          column: {
            stacking: "normal",
            events: {
              click: e => {
                const point = e.point;
                const from = point.from;
                const to = point.to;

                let status = point.status;

                if (this.statusMode === this.$options.statusModes.group) {
                  status = statusGroups[status];
                  this.statusMode = this.$options.statusModes.individual;
                } else {
                  status = [status];
                }

                this.$emit("filter", {
                  comment: status,
                  releaseDateStart: from,
                  releaseDateEnd: to
                });
              }
            }
          }
        },
        series
      });
    }
  }
};
</script>

<style scoped>
.loading-overlay.is-active {
  z-index: 9;
}
#containerhc {
  height: 500px;
  min-width: 310px;
  max-width: 800px;
  margin: 0 auto;
}
.field-body div,
.is-horizontal {
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>



// WEBPACK FOOTER //
// src/components/pages/Orders/list-new/partials/dashboard.vue